import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { finalize } from 'rxjs';
import { Table } from 'primeng/table';
import { MatDialog } from '@angular/material/dialog';

import { MoverLancamentoDialogComponent } from '../../../movimentacao-caixa/components/dialogs/mover-lancamento-dialog/mover-lancamento-dialog.component';
import { TransferirLancamentoDialogComponent } from '@components/transferir-lancamento-dialog/transferir-lancamento-dialog.component';
import { CadastroLoteLancamentosDialogComponent, CadastroLoteLancamentosDialogData } from '../../../../../shared/dialogs/cadastro-lote-lancamentos-dialog/cadastro-lote-lancamentos-dialog.component';
import { ExibeCorrespondenciaDialogComponent } from '../../../../../shared/dialogs/exibe-correspondencia-dialog/exibe-correspondencia-dialog.component';
import { ConciliacaoApiService } from '../../../conciliacao/services/conciliacao-api.service';
import { ConciliacaoService } from '../../../conciliacao/services/conciliacao.service';
import { Lancamento } from '../../../conciliacao/models/lancamento';

import { Permissao } from '@models/enums/permissao';
import { RelatorioFinanceiroApiService } from '../../services/relatorio-financeiro-api.service';
import { MessageService } from '@services/message.service';
import { DownloadService } from '@services/download.service';
import { TipoBuscaEnum } from '../../models/tipo-busca.enum';
import { ConsultaLancamentosOutput } from '../../models/consulta-lancamentos-output';
import { TipoLancamento } from '@models/tipo-lancamento';
import { LancamentoRelatorioFinanceiro } from '../../models/lancamento-relatorio-financeiro';
import { LancamentoEditRelatorioFinanceiro } from '../../models/lancamento-edit-relatorio-financeiro';

interface EdicaoLancamentoState {
  lancamento: LancamentoRelatorioFinanceiro;
  clone: LancamentoRelatorioFinanceiro;
}

@Component({
  selector: 'app-relatorio-lancamentos',
  templateUrl: './relatorio-lancamentos.component.html',
  styleUrl: './relatorio-lancamentos.component.scss',
  standalone: false,
})
export class RelatorioLancamentosComponent implements OnChanges {

  protected get permissaoEditarLancamento() { return Permissao.EDITAR_LANCAMENTO_CAIXA; }

  @ViewChild('dtLancamentos') dtLancamentos!: Table;

  @Input() resultado!: ConsultaLancamentosOutput;
  @Input() tiposLancamento: TipoLancamento[] = [];
  @Output() refreshar = new EventEmitter<void>();

  exportandoExcel = false;
  lancamentosEmEdicao: Map<number, EdicaoLancamentoState> = new Map();
  modoSelecao = false;
  lancamentosSelecionados: LancamentoRelatorioFinanceiro[] = [];

  constructor(
    private relatorioFinanceiroApiService: RelatorioFinanceiroApiService,
    private messageService: MessageService,
    private downloadService: DownloadService,
    private dialog: MatDialog,
    private conciliacaoApiService: ConciliacaoApiService,
    private conciliacaoService: ConciliacaoService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resultado']) {
      this.cancelarModoSelecao();
      this.lancamentosEmEdicao.clear();
    }
  }

  get exibirColunasCategoria(): boolean {
    const tipo = this.resultado?.parametrosBusca?.tipoBusca;
    return tipo === TipoBuscaEnum.RelatorioCaixa || tipo === TipoBuscaEnum.CategoriaRelatorio;
  }

  get quantidadeColunas(): number {
    let cols = 7; // fixas: data, valor, conta, tipo, lote, observação, ações
    if (this.resultado?.ehContaBanco) cols++;
    if (this.exibirColunasCategoria) cols += 2;
    if (this.modoSelecao) cols++;
    return cols;
  }

  isLancamentoSelecionavel = ({ data }: { data: LancamentoRelatorioFinanceiro }) => !data.conciliado;

  public ativarModoSelecao(): void {
    this.lancamentosEmEdicao.clear();
    this.modoSelecao = true;
    this.lancamentosSelecionados = [];
  }

  public cancelarModoSelecao(): void {
    this.modoSelecao = false;
    this.lancamentosSelecionados = [];
  }

  public alterarTipoEmMassaClick(): void {
    if (this.lancamentosSelecionados.length === 0) {
      this.messageService.showToastWarning('Selecione pelo menos 1 lançamento');
      return;
    }

    const primeiroTipoId = this.lancamentosSelecionados[0].tipoLancamento.id;
    const todosMesmoTipo = this.lancamentosSelecionados.every(l => l.tipoLancamento.id === primeiroTipoId);

    const dialogRef = this.dialog.open(TransferirLancamentoDialogComponent, {
      width: '580px',
      panelClass: 'no-padding-dialog',
      disableClose: true,
      data: {
        lancamentoIds: this.lancamentosSelecionados.map(l => l.id),
        tiposLancamento: this.tiposLancamento,
        tipoLancamentoIdAtual: todosMesmoTipo ? primeiroTipoId : undefined,
      },
    });

    dialogRef.afterClosed().subscribe((success: boolean) => {
      if (success) {
        this.cancelarModoSelecao();
        this.refreshar.emit();
      }
    });
  }

  public alterarDataEmMassaClick(): void {
    if (this.lancamentosSelecionados.length === 0) {
      this.messageService.showToastWarning('Selecione pelo menos 1 lançamento');
      return;
    }

    const primeiroLancamento = this.lancamentosSelecionados[0];
    const dialogRef = this.dialog.open(MoverLancamentoDialogComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      disableClose: true,
      data: {
        lancamentoIds: this.lancamentosSelecionados.map(l => l.id),
        data: new Date(primeiroLancamento.data),
      },
    });

    dialogRef.afterClosed().subscribe((success: boolean) => {
      if (success) {
        this.cancelarModoSelecao();
        this.refreshar.emit();
      }
    });
  }

  public onRowEditInit(lancamento: LancamentoRelatorioFinanceiro): void {
    this.lancamentosEmEdicao.set(lancamento.id, { lancamento, clone: { ...lancamento } });
  }

  public onRowEditSave(lancamento: LancamentoRelatorioFinanceiro): void {
    const dto: LancamentoEditRelatorioFinanceiro = {
      id: lancamento.id,
      tipoLancamentoId: lancamento.tipoLancamento.id,
      valor: lancamento.valor,
      observacao: lancamento.observacao,
    };
    this.relatorioFinanceiroApiService.editarLancamento(dto).subscribe({
      next: () => {
        lancamento.nomeConta = lancamento.tipoLancamento.nomeConta;
        this.messageService.showToastSuccess('Salvo com sucesso.');
        this.lancamentosEmEdicao.delete(lancamento.id);
      },
    });
  }

  public onRowEditCancel(lancamento: LancamentoRelatorioFinanceiro): void {
    const estado = this.lancamentosEmEdicao.get(lancamento.id);
    if (estado) {
      lancamento.valor = estado.clone.valor;
      lancamento.tipoLancamento = estado.clone.tipoLancamento;
      lancamento.observacao = estado.clone.observacao;
    }
    this.dtLancamentos.cancelRowEdit(lancamento);
    this.lancamentosEmEdicao.delete(lancamento.id);
  }

  public visualizarLote(lancamento: LancamentoRelatorioFinanceiro): void {
    this.dialog.open(CadastroLoteLancamentosDialogComponent, {
      width: '1400px',
      maxWidth: '95vw',
      panelClass: 'no-padding-dialog',
      disableClose: true,
      data: {
        data: new Date(lancamento.data),
        nomeLote: lancamento.nomeLoteLancamentos,
      } as CadastroLoteLancamentosDialogData,
    });
  }

  public visualizarCorrespondencia(lancamento: LancamentoRelatorioFinanceiro): void {
    this.conciliacaoApiService.getDadosConciliacaoExistente(lancamento.conciliacaoId!).subscribe((dados) => {
      this.conciliacaoService.setDadosConciliacao(dados);
      const correspondenciaIndex = this.conciliacaoService.getCorrespondenciaIndex({ id: lancamento.id } as Lancamento, false);

      this.dialog.open(ExibeCorrespondenciaDialogComponent, {
        width: '1200px',
        maxWidth: '95vw',
        panelClass: 'no-padding-dialog',
        autoFocus: false,
        data: {
          acaoDesfazer: false,
          correspondenciaIndex: correspondenciaIndex,
        },
      });
    });
  }

  public exportarExcel(): void {
    this.exportandoExcel = true;
    this.relatorioFinanceiroApiService.exportarExcel(this.resultado.parametrosBusca)
      .pipe(finalize(() => this.exportandoExcel = false))
      .subscribe({
        next: (blob) => {
          const file = new File([blob], 'relatorio_financeiro.xlsx', {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          this.downloadService.downloadFile(file, 'relatorio_financeiro.xlsx');
          this.messageService.showToastSuccess('Download realizado com sucesso.');
        },
      });
  }
}
